import React from "react";
import {Controller, SubmitHandler, useForm} from "react-hook-form";

import SubTitle from "../../components/SubTitle";

import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import {
  Box,
  Button,
  Grid,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  TextField,
  TextareaAutosize,
  Typography,
  styled,
} from "@mui/material";
import {useRecoilState, useRecoilValue, useSetRecoilState} from "recoil";
import {tokenSelector, userCompanyData} from "../../Recoil/Atom/authState";
import * as workOrderApi from "../../api/workOrderApi/workOrderApi";
import ConfirmButtonGroup from "../../components/ConfirmButtonGroup";
import MWCaption from "../../components/MWCaption";
import {useConvertBase64} from "../../hooks/useConvertBase64";
import * as Params from "../../models/clientModels/interfaces/apiParams";

import FlagCircleIcon from "@mui/icons-material/FlagCircle";
import {useQueryClient} from "react-query";
import {useNavigate} from "react-router-dom";
import {snackBarState, snackBarToggleState} from "../../Recoil/Atom/snackBarState";
import {useUpdateMutation} from "../../hooks/useQueryHooks";
import {ResponseItem} from "../../models/clientModels/interfaces/Response";
import {EQCompanyData} from "../../models/serverModels/EQCompanyData";
import {yupResolver} from "@hookform/resolvers/yup";
import {companyDataSchema} from "../../validation/companyDataSchema";

const ManageTableContainer = styled(Grid)(({theme}) => ({
  marginTop: "32px",
  flexFlow: "column",
  justifyContent: "center",
  alignItems: "center",
  backgroundColor: theme.palette.background.paper,
}));

export const ManageTable = styled(TableContainer)(({theme}) => ({
  width: "50%",
  marginTop: "48px",
  marginBottom: "32px",
  paddingLeft: "16px",
  paddingTop: "32px",
  paddingRight: "16px",
  paddingBottom: "32px",
  backgroundColor: theme.palette.gray.main,
  boxShadow: `rgb(0 0 0 / 10%) 0rem 0.25rem 0.375rem -0.0625rem, rgb(0 0 0 / 6%) 0rem 0.125rem 0.25rem -0.0625rem`,

  "& .subTitle": {
    fontSize: "18px",
    fontWeight: 600,
  },
  "& table th, td": {
    border: `1px solid ${theme.palette.warning.main}`,
    [theme.breakpoints.down("sm")]: {
      padding: "8px",
    },
  },
  "& tbody th": {
    padding: "8px",
    fontWeight: 700,
    backgroundColor: theme.palette.custom.light,
  },
  "& textarea": {
    lineHeight: 1.5,
  },

  [theme.breakpoints.down("lg")]: {
    width: "80%",
    padding: "8px",
  },
  [theme.breakpoints.down("sm")]: {
    width: "100%",
    padding: 0,
  },
}));

const ManageChangeTip = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const {convertChangeHandler, image} = useConvertBase64();

  const tokenState = useRecoilValue(tokenSelector);

  const [companyData, setCompanyData] = useRecoilState(userCompanyData);
  const setSnackBarState = useSetRecoilState(snackBarState);
  const setSnackBarToggle = useSetRecoilState(snackBarToggleState);

  const {mutate: updateMutate} = useUpdateMutation<
    ResponseItem<EQCompanyData>,
    Params.Update<EQCompanyData>
  >({
    api: workOrderApi.ChangeTipUpdate,
    options: {
      onSuccess: (data: ResponseItem<EQCompanyData>) => {
        setCompanyData(data?.item!);
        setSnackBarToggle(true);
        setSnackBarState("update");
        navigate("/WorkOrder/ChangeRequestList");
        queryClient.invalidateQueries("ChangeRequestList");
      },
    },
  });

  const {
    control,
    handleSubmit,
    setValue,
    getValues,
    formState: {errors},
  } = useForm<EQCompanyData>({
    defaultValues: companyData,
    resolver: yupResolver(companyDataSchema),
  });

  const onSubmit: SubmitHandler<EQCompanyData> = (data) => {
    updateMutate({
      token: tokenState,
      item: {...companyData, ...data},
    });
  };

  return (
    <React.Fragment>
      <SubTitle subTitle="변경요청서 작성 요령" />
      <form onSubmit={handleSubmit(onSubmit)}>
        <ManageTableContainer container rowSpacing={4}>
          <MWCaption title="변경요청서 작성 요령" color="#880E4F" icon={<FlagCircleIcon />} />
          <ManageTable>
            <Box sx={{margin: "auto", width: "100%"}}>
              <Table>
                <TableBody>
                  <TableRow>
                    <TableCell component="th" width="25%">
                      변경판단기준파일
                    </TableCell>
                    <TableCell>
                      <Controller
                        name="base64"
                        control={control}
                        render={({field: {onChange, name, value}}) => (
                          <>
                            <Grid
                              container
                              alignItems="center"
                              flexWrap="nowrap"
                              width="100%"
                              pt="8px"
                              pb="4px"
                            >
                              <Button
                                variant="outlined"
                                component="label"
                                size="small"
                                sx={{whiteSpace: "nowrap", flexShrink: 0}}
                              >
                                파일 선택
                                <TextField
                                  sx={{display: "none"}}
                                  onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                                    convertChangeHandler(event, setValue, name, "changeGuideFile")
                                  }
                                  type="file"
                                  id="base64"
                                  size="small"
                                  hidden
                                />
                              </Button>
                              <Typography
                                variant="subtitle2"
                                sx={{marginLeft: "8px", lineHeight: 1}}
                              >
                                {getValues("changeGuideFile")}
                                {getValues("changeGuideFile") && (
                                  <IconButton
                                    aria-label="delete"
                                    color="secondary"
                                    onClick={() => {
                                      setValue("base64", "");
                                      setValue("changeGuideFile", "");
                                    }}
                                  >
                                    <HighlightOffIcon />
                                  </IconButton>
                                )}
                              </Typography>
                            </Grid>
                            {errors.base64 && <Typography>{`errors.base64.message`}</Typography>}
                          </>
                        )}
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell component="th">변경구분 설명</TableCell>
                    <TableCell>
                      <Controller
                        name="changeRequestTip1"
                        control={control}
                        render={({field: {onChange, value}}) => (
                          <TextareaAutosize
                            defaultValue={value}
                            aria-label="minimum height"
                            minRows={6}
                            onChange={onChange}
                            style={{width: "100%", padding: "8px"}}
                          />
                        )}
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell component="th">변경등급 설명</TableCell>
                    <TableCell>
                      <Controller
                        name="changeRequestTip2"
                        control={control}
                        render={({field: {onChange, value}}) => (
                          <TextareaAutosize
                            defaultValue={value}
                            aria-label="minimum height"
                            minRows={6}
                            onChange={onChange}
                            style={{width: "100%", padding: "8px"}}
                          />
                        )}
                      />
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </Box>
          </ManageTable>
        </ManageTableContainer>
        <Box sx={{margin: "auto"}}>
          <ConfirmButtonGroup
            link={`/WorkOrder/ChangeRequestList`}
            cancelText="취소"
            confirmText="수정"
          />
        </Box>
      </form>
    </React.Fragment>
  );
};

export default ManageChangeTip;
